import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBook,
  faPlus,
  faEdit,
  faTrash,
  faSave,
  faFileAlt,
  faFolder,
  faFolderOpen,
  faChevronDown,
  faChevronRight,
  faTimes,
  faCheck,
  faDownload,
  faPrint,
} from '@fortawesome/free-solid-svg-icons';
import styled from 'styled-components';
import {
  tokens,
  PageShell,
  PageWidth,
  PageHeader,
  Card,
  Button,
  SearchInput,
  Input,
  Textarea,
  Label,
  Field,
  Select,
  SideList,
  EmptyState,
} from '../components/admin/AdminUI';

const c = tokens.color;

/* 좌측 카테고리/매뉴얼 트리 */
const TreeBlock = styled.div`
  margin-top: 4px;
`;

const CategoryRow = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  text-align: left;
  font-family: inherit;
  font-size: 14px;
  font-weight: ${(p) => (p.$selected ? 600 : 500)};
  color: ${(p) => (p.$selected ? c.primary : c.text)};
  background: ${(p) => (p.$selected ? c.primarySoft : 'transparent')};
  border: none;
  border-radius: ${tokens.radius.sm};
  padding: 9px 12px;
  margin-bottom: 2px;
  cursor: pointer;
  transition: background-color .12s ease, color .12s ease;
  &:hover { background: ${(p) => (p.$selected ? c.primarySoft : c.surfaceMuted)}; }
  & .chev { width: 12px; color: ${c.subtle}; flex: 0 0 auto; }
`;

const ManualRow = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  text-align: left;
  font-family: inherit;
  font-size: 13px;
  font-weight: ${(p) => (p.$selected ? 600 : 400)};
  color: ${(p) => (p.$selected ? c.primary : c.muted)};
  background: ${(p) => (p.$selected ? c.primarySoft : 'transparent')};
  border: none;
  border-left: 2px solid ${(p) => (p.$selected ? c.primary : 'transparent')};
  border-radius: 0 ${tokens.radius.sm} ${tokens.radius.sm} 0;
  padding: 8px 12px;
  margin: 0 0 2px ${(p) => (p.level || 1) * 18}px;
  cursor: pointer;
  transition: background-color .12s ease, color .12s ease;
  &:hover { background: ${(p) => (p.$selected ? c.primarySoft : c.surfaceMuted)}; }
`;

/* 우측 콘텐츠 헤더 */
const ContentHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${c.border};
`;

const ContentTitle = styled.h2`
  margin: 0;
  flex: 1;
  min-width: 0;
  color: ${c.heading};
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.01em;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

/* 읽기용 마크다운 본문 (Clean SaaS 가독성) */
const ContentBody = styled.div`
  line-height: 1.7;
  color: ${c.text};
  font-size: 14px;

  h1, h2, h3, h4, h5, h6 {
    margin-top: 24px;
    margin-bottom: 12px;
    color: ${c.heading};
    font-weight: 600;
    letter-spacing: -0.01em;
  }

  p { margin-bottom: 16px; color: ${c.text}; }

  ul, ol { margin-bottom: 16px; padding-left: 24px; color: ${c.text}; }

  li { margin-bottom: 4px; }

  code {
    background-color: ${c.surfaceMuted};
    color: ${c.heading};
    padding: 2px 6px;
    border-radius: 6px;
    font-family: 'Courier New', monospace;
    font-size: 13px;
  }

  pre {
    background-color: ${c.surfaceMuted};
    padding: 16px;
    border-radius: ${tokens.radius.sm};
    overflow-x: auto;
    margin-bottom: 16px;
    border: 1px solid ${c.border};
    border-left: 3px solid ${c.primary};
  }

  blockquote {
    border-left: 3px solid ${c.primary};
    background: ${c.primarySoft};
    padding: 12px 16px;
    border-radius: 0 ${tokens.radius.sm} ${tokens.radius.sm} 0;
    margin: 16px 0;
    color: ${c.text};
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: ${tokens.radius.sm};
    margin: 16px 0;
    box-shadow: ${tokens.shadow.xs};
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 16px 0;

    th, td {
      border-bottom: 1px solid ${c.borderSubtle};
      padding: 12px 14px;
      text-align: left;
      font-size: 14px;
      color: ${c.text};
    }

    th {
      background-color: ${c.surfaceMuted};
      font-weight: 600;
      font-size: 13px;
      color: ${c.muted};
      border-bottom: 1px solid ${c.border};
    }
  }
`;

/* 에디터 */
const EditorContainer = styled.div`
  border: 1px solid ${c.border};
  border-radius: ${tokens.radius.sm};
  overflow: hidden;
  background: #fff;
  transition: border-color .15s ease, box-shadow .15s ease;
  &:focus-within {
    border-color: ${c.primary};
    box-shadow: 0 0 0 4px ${c.primaryRing};
  }
`;

const EditorToolbar = styled.div`
  padding: 10px 12px;
  border-bottom: 1px solid ${c.border};
  background-color: ${c.surfaceMuted};
  display: flex;
  gap: 8px;
  align-items: center;
`;

const EditorContent = styled.textarea`
  width: 100%;
  min-height: 320px;
  padding: 16px;
  border: none;
  resize: vertical;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.6;
  color: ${c.heading};
  box-sizing: border-box;
  &::placeholder { color: ${c.subtle}; }
  &:focus { outline: none; }
`;

/* 2열 레이아웃 */
const MainLayout = styled.div`
  display: grid;
  grid-template-columns: 300px minmax(0, 1fr);
  gap: 20px;
  align-items: stretch;
  min-height: calc(100vh - 200px);

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    min-height: 0;
  }
`;

/* 모달 */
const Modal = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(16, 24, 40, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 16px;
`;

const ModalContent = styled.div`
  background: #fff;
  border: 1px solid ${c.border};
  border-radius: ${tokens.radius.lg};
  padding: 24px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: ${tokens.shadow.popover};
  font-family: ${tokens.font};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${c.border};
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: ${c.heading};
  font-size: 16px;
  font-weight: 600;
`;

const SystemGuide = () => {
  const [categories, setCategories] = useState([]);
  const [manuals, setManuals] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedManual, setSelectedManual] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'category' | 'manual'
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  const [newItem, setNewItem] = useState({
    title: '',
    content: '',
    categoryId: null,
    parentId: null,
    order: 0
  });

  // 샘플 데이터
  const sampleCategories = [
    { id: 1, name: '시작하기', icon: 'book', order: 1 },
    { id: 2, name: '게시판 관리', icon: 'folder', order: 2 },
    { id: 3, name: '사용자 관리', icon: 'users', order: 3 },
    { id: 4, name: '시스템 설정', icon: 'cog', order: 4 },
    { id: 5, name: 'FAQ', icon: 'question', order: 5 }
  ];

  const sampleManuals = [
    {
      id: 1,
      title: '시스템 개요',
      content: `# 시스템 개요

## 소개
이 시스템은 통합 게시판 관리 시스템입니다.

## 주요 기능
- **동적 게시판 생성**: 테이블 구조에 따라 자동으로 게시판 생성
- **권한 관리**: 조직별, 사용자별 세분화된 권한 설정
- **관계 테이블 지원**: 복잡한 조인 쿼리와 CTE 지원

## 시스템 요구사항
- Node.js 16.0 이상
- PostgreSQL 12.0 이상
- React 18.0 이상

\`\`\`javascript
// 예제 코드
const config = {
  database: 'myapp',
  host: 'localhost',
  port: 5432
};
\`\`\`

> **참고**: 시스템 설치 전 모든 요구사항을 확인해주세요.`,
      categoryId: 1,
      order: 1,
      updatedAt: '2024-01-15'
    },
    {
      id: 2,
      title: '게시판 생성하기',
      content: `# 게시판 생성하기

## 1. 게시판 관리 페이지 접속
\`관리자 메뉴 > 게시판 관리\`에서 새 게시판을 생성할 수 있습니다.

## 2. 테이블 선택
- 기존 테이블을 선택하거나
- 새 테이블을 생성합니다

## 3. 컬럼 설정
각 컬럼의 표시 여부와 속성을 설정합니다:
- **표시명**: 화면에 표시될 컬럼명
- **정렬**: 컬럼별 정렬 가능 여부
- **검색**: 검색 대상 컬럼 설정

## 4. 권한 설정
게시판별 접근 권한을 설정합니다.`,
      categoryId: 2,
      order: 1,
      updatedAt: '2024-01-14'
    },
    {
      id: 3,
      title: '관계 테이블 설정',
      content: `# 관계 테이블 설정

## 개요
복잡한 데이터베이스 관계를 처리하기 위한 설정 방법입니다.

## Custom Query 설정
사용자 정의 쿼리를 통해 복잡한 조인을 처리할 수 있습니다.

\`\`\`sql
SELECT p.title, p.content, u.name as author_name
FROM posts p
LEFT JOIN users u ON p.user_id = u.id
WHERE p.status = 'published'
\`\`\`

## CTE (Common Table Expression)
재사용 가능한 쿼리 블록을 정의할 수 있습니다.

\`\`\`sql
WITH recent_posts AS (
    SELECT * FROM posts
    WHERE created_at > NOW() - INTERVAL '30 days'
)
SELECT * FROM recent_posts WHERE status = 'active'
\`\`\``,
      categoryId: 2,
      order: 2,
      updatedAt: '2024-01-13'
    }
  ];

  useEffect(() => {
    setCategories(sampleCategories);
    setManuals(sampleManuals);
  }, []);

  const filteredManuals = manuals.filter(manual =>
    manual.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    manual.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleCategory = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setSelectedManual(null);
    setEditMode(false);
  };

  const handleManualSelect = (manual) => {
    setSelectedManual(manual);
    setEditMode(false);
  };

  const handleEdit = () => {
    setEditMode(true);
    if (selectedManual) {
      setNewItem({
        title: selectedManual.title,
        content: selectedManual.content,
        categoryId: selectedManual.categoryId,
        parentId: selectedManual.parentId,
        order: selectedManual.order
      });
    }
  };

  const handleSave = async () => {
    try {
      const url = editMode && selectedManual ? '/request/updateManual' : '/request/createManual';
      const payload = editMode && selectedManual
        ? { ...newItem, id: selectedManual.id }
        : newItem;

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        alert('저장되었습니다!');
        setEditMode(false);
        // 데이터 새로고침 로직
      }
    } catch (error) {
      console.error('Error saving manual:', error);
      alert('저장 중 오류가 발생했습니다.');
    }
  };

  const handleDelete = async () => {
    if (!selectedManual) return;

    if (window.confirm('정말로 삭제하시겠습니까?')) {
      try {
        const response = await fetch('/request/deleteManual', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ id: selectedManual.id })
        });

        if (response.ok) {
          alert('삭제되었습니다!');
          setSelectedManual(null);
          // 데이터 새로고침 로직
        }
      } catch (error) {
        console.error('Error deleting manual:', error);
        alert('삭제 중 오류가 발생했습니다.');
      }
    }
  };

  const openModal = (type) => {
    setModalType(type);
    setShowModal(true);
    setNewItem({
      title: '',
      content: '',
      categoryId: selectedCategory?.id || null,
      parentId: null,
      order: 0
    });
  };

  const categoryOptions = categories.map((cat) => ({
    value: cat.id,
    label: cat.name,
  }));

  const renderContent = () => {
    if (editMode) {
      return (
        <div>
          <Field>
            <Label>제목</Label>
            <Input
              value={newItem.title}
              onChange={(e) => setNewItem(prev => ({...prev, title: e.target.value}))}
              placeholder="매뉴얼 제목을 입력하세요"
            />
          </Field>

          <Field>
            <Label>카테고리</Label>
            <Select
              value={newItem.categoryId || ''}
              onChange={(v) => setNewItem(prev => ({...prev, categoryId: parseInt(v)}))}
              options={categoryOptions}
              placeholder="카테고리 선택"
              grow="1 1 100%"
            />
          </Field>

          <Field>
            <Label>내용</Label>
            <EditorContainer>
              <EditorToolbar>
                <Button $size="sm"><FontAwesomeIcon icon={faFileAlt} /> Markdown 지원</Button>
              </EditorToolbar>
              <EditorContent
                value={newItem.content}
                onChange={(e) => setNewItem(prev => ({...prev, content: e.target.value}))}
                placeholder="Markdown 형식으로 내용을 작성하세요..."
              />
            </EditorContainer>
          </Field>

          <ActionButtons>
            <Button $variant="primary" onClick={handleSave}>
              <FontAwesomeIcon icon={faSave} /> 저장
            </Button>
            <Button onClick={() => setEditMode(false)}>
              <FontAwesomeIcon icon={faTimes} /> 취소
            </Button>
          </ActionButtons>
        </div>
      );
    }

    if (selectedManual) {
      return (
        <div>
          <ContentHead>
            <ContentTitle>{selectedManual.title}</ContentTitle>
            <ActionButtons>
              <Button $variant="primary" onClick={handleEdit}>
                <FontAwesomeIcon icon={faEdit} /> 편집
              </Button>
              <Button $variant="dangerSoft" onClick={handleDelete}>
                <FontAwesomeIcon icon={faTrash} /> 삭제
              </Button>
              <Button>
                <FontAwesomeIcon icon={faDownload} /> 다운로드
              </Button>
              <Button>
                <FontAwesomeIcon icon={faPrint} /> 인쇄
              </Button>
            </ActionButtons>
          </ContentHead>

          <ContentBody
            dangerouslySetInnerHTML={{
              __html: selectedManual.content.replace(/\n/g, '<br>')
            }}
          />
        </div>
      );
    }

    return (
      <EmptyState
        icon={<FontAwesomeIcon icon={faBook} />}
        title="매뉴얼을 선택하세요"
        description="좌측에서 카테고리나 매뉴얼을 선택하면 내용을 확인할 수 있습니다."
      />
    );
  };

  return (
    <PageShell>
      <PageWidth>
        <PageHeader
          eyebrow="SYSTEM"
          title="시스템 매뉴얼"
          subtitle="시스템 사용법과 가이드를 확인하세요."
          actions={
            <>
              <Button $variant="secondary" onClick={() => openModal('category')}>
                <FontAwesomeIcon icon={faPlus} /> 카테고리 추가
              </Button>
              <Button $variant="primary" onClick={() => openModal('manual')}>
                <FontAwesomeIcon icon={faPlus} /> 매뉴얼 추가
              </Button>
            </>
          }
        />

        <MainLayout>
          {/* 좌측: 카테고리 및 매뉴얼 목록 */}
          <Card $pad="16px">
            <SearchInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="매뉴얼 검색..."
              grow="1 1 100%"
            />

            <TreeBlock>
              <SideList as="div">
                {categories.map((category) => (
                  <div key={category.id}>
                    <CategoryRow
                      type="button"
                      $selected={selectedCategory?.id === category.id}
                      onClick={() => {
                        handleCategorySelect(category);
                        toggleCategory(category.id);
                      }}
                    >
                      <FontAwesomeIcon
                        className="chev"
                        icon={expandedCategories.has(category.id) ? faChevronDown : faChevronRight}
                      />
                      <FontAwesomeIcon
                        icon={expandedCategories.has(category.id) ? faFolderOpen : faFolder}
                      />
                      {category.name}
                    </CategoryRow>

                    {expandedCategories.has(category.id) && (
                      <div>
                        {filteredManuals
                          .filter(manual => manual.categoryId === category.id)
                          .map((manual) => (
                            <ManualRow
                              key={manual.id}
                              type="button"
                              level={1}
                              $selected={selectedManual?.id === manual.id}
                              onClick={() => handleManualSelect(manual)}
                            >
                              <FontAwesomeIcon icon={faFileAlt} />
                              {manual.title}
                            </ManualRow>
                          ))}
                      </div>
                    )}
                  </div>
                ))}
              </SideList>
            </TreeBlock>
          </Card>

          {/* 우측: 컨텐츠 영역 */}
          <Card $pad="24px">
            {renderContent()}
          </Card>
        </MainLayout>

        {/* 모달 */}
        {showModal && (
          <Modal onClick={() => setShowModal(false)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>
                  {modalType === 'category' ? '새 카테고리 추가' : '새 매뉴얼 추가'}
                </ModalTitle>
                <Button $variant="ghost" onClick={() => setShowModal(false)}>
                  <FontAwesomeIcon icon={faTimes} />
                </Button>
              </ModalHeader>

              <Field>
                <Label>{modalType === 'category' ? '카테고리명' : '매뉴얼 제목'}</Label>
                <Input
                  value={newItem.title}
                  onChange={(e) => setNewItem(prev => ({...prev, title: e.target.value}))}
                  placeholder={modalType === 'category' ? '카테고리명 입력' : '매뉴얼 제목 입력'}
                />
              </Field>

              {modalType === 'manual' && (
                <>
                  <Field>
                    <Label>카테고리</Label>
                    <Select
                      value={newItem.categoryId || ''}
                      onChange={(v) => setNewItem(prev => ({...prev, categoryId: parseInt(v)}))}
                      options={categoryOptions}
                      placeholder="카테고리 선택"
                      grow="1 1 100%"
                    />
                  </Field>

                  <Field>
                    <Label>내용</Label>
                    <Textarea
                      value={newItem.content}
                      onChange={(e) => setNewItem(prev => ({...prev, content: e.target.value}))}
                      placeholder="매뉴얼 내용을 입력하세요..."
                      style={{ minHeight: '200px' }}
                    />
                  </Field>
                </>
              )}

              <ActionButtons>
                <Button $variant="primary" onClick={handleSave}>
                  <FontAwesomeIcon icon={faCheck} /> 저장
                </Button>
                <Button onClick={() => setShowModal(false)}>
                  <FontAwesomeIcon icon={faTimes} /> 취소
                </Button>
              </ActionButtons>
            </ModalContent>
          </Modal>
        )}
      </PageWidth>
    </PageShell>
  );
};

export default SystemGuide;
